import { useTranslation } from "next-i18next";
import PropTypes from "prop-types";
import { useFilter } from "./hooks/useFilter";
import { titleCase } from "@lib/formatString";
import { CheckIcon } from "@components/Icon";
import {
  COLOUR_TAGS,
  SIZE,
  TYPE,
  STANDARD_SIZES,
  DRESS_SIZES,
  PANT_SIZES,
  BEDDING_SIZES,
} from "./constants";
import styles from "./styles/FilterList.module.css";

export const FilterList = ({ 
  filterName, 
  filterLabels, 
  filteredValues,
  onChange,
  searchView = false 
}) => {
  const { t } = useTranslation("filter");
  const { handleChange, clearValues, updatedValues } = useFilter({
    filterName,
    filterLabels,
    filteredValues,
    onChange,
  });

  const title = (key) => {
    if (key === COLOUR_TAGS) return t("all-colours-label");
    if (searchView) {
      const translatedKey = t(key);
      return `${titleCase(translatedKey)}`;
    }
    const translatedKey = t(key);
      return `${titleCase(translatedKey)}:`;
    
  };

  const formatColour = (key) => {
    if (key.endsWith("s")) return titleCase(key);
    return `${titleCase(key)}`;
  };

  const listTitle = (key) => {
    if (filterName === COLOUR_TAGS) return formatColour(key);
    if (filterName === TYPE) return trimType(key);
    return titleCase(key);
  };

  const trimType = (key) => {
    const isolatedType = key
      .replace("type:", "")
      .replace("type-fr:", "")
      .trim();
    return titleCase(isolatedType);
  };

  // creates different list sections for sizing
  const sizeLists = { standard: [], dress: [], pants: [], bedding: [] };

  STANDARD_SIZES.forEach((size) => {
    if (filterLabels.includes(size.toLowerCase())) {
      sizeLists.standard.push(size);
    }
  });

  DRESS_SIZES.forEach((size) => {
    if (filterLabels.includes(size.toLowerCase())) {
      sizeLists.dress.push(size);
    }
  });

  PANT_SIZES.forEach((size) => {
    if (filterLabels.includes(size.toLowerCase())) {
      sizeLists.pants.push(size);
    }
  });

  BEDDING_SIZES.forEach((size) => {
    if (filterLabels.includes(size.toLowerCase())) {
      sizeLists.bedding.push(size);
    }
  });

  const sectionExists = (value) =>
    value.toLowerCase() !== "default" && value.toLowerCase() !== "n/a";

  const Header = () => {
    const headerStyles = `
      ${searchView ? styles.searchHeading : styles.heading}
    `;
    return (
      <header className={headerStyles}>
        <h4>{t(title(filterName))}</h4>
        {filteredValues.length !== 0 && (
          <button className={styles.reset} onClick={clearValues}>
            {t("reset-button")}
          </button>
        )}
      </header>
    );
  };

  const WordLists = () => {
    return (
      <ul className={styles.list}>
        {filterLabels.map((value) => {
          return (
            <li className={styles.listItem} key={value.toLowerCase()}>
              <button className={styles.button} onClick={() => handleChange(value.toLowerCase())}>
                {listTitle(value)}
                {updatedValues[value.toLowerCase()] && <CheckIcon />}
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  const WordListsPill = () => {

    return (
      <ul className={styles.pillList}>
        {filterLabels.map((value) => {

        const pillListItemStyles = `
          ${styles.pillListItem}
          ${updatedValues[value.toLowerCase()] ? styles.pillSelected : ""}
        `;

          return (
            <li className={pillListItemStyles} key={value.toLowerCase()}>
              <button className={styles.pillButton} onClick={() => handleChange(value.toLowerCase())}>
                {t(listTitle(value))}
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  const SizeListSections = ({ list }) => {
    return (
      <ul className={styles.sizeList} key={list}>
        {sizeLists[list].map((value) => {
          const listItemStyles = `
            ${styles.sizeListItem}
            ${updatedValues[value.toLowerCase()] ? styles.selected : ""}
          `;

          if (sectionExists(value)) {
            return (
              <li className={listItemStyles} key={value.toLowerCase()}>
                <button
                  className={styles.sizeButton}
                  onClick={() => handleChange(value.toLowerCase())}
                >
                  {value}
                </button>
              </li>
            );
          }
        })}
      </ul>
    );
  };

  const SizeList = () => {
    return (
      <div className={styles.sizeContainer}>
        {Object.keys(sizeLists).map((list) => {
          if (sizeLists[list].length) {
            return (
              <div key={list} className={styles.listContainer}>
                <p>{titleCase(t(list))}</p>
                <SizeListSections list={list} />
              </div>
            );
          }
        })}
      </div>
    );
  };

  const containerStyles = `
    ${searchView ? "" : styles.withPadding }
    ${styles.container}
  `;

  return (
    <div className={containerStyles}>
      <Header />
      {filterName !== SIZE && !searchView && <WordLists />}
      {filterName !== SIZE && searchView && <WordListsPill />}
      {filterName === SIZE && !searchView && <SizeList />}
      {filterName === SIZE && searchView && <WordListsPill />}
    </div>
  );
};

FilterList.propTypes = {
  filterName: PropTypes.string,
  filterlabels: PropTypes.arrayOf(PropTypes.string),
  filteredValues: PropTypes.array,
  onChange: PropTypes.func,
};
